import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { BookOpen, Calendar } from 'lucide-react';

interface Assignment {
  id: string;
  serviceDate: string;
  serviceName: string | null;
  parsha: string | null;
  notes: string | null;
  status: string;
  honor: {
    id: string;
    name: string;
    hebrewName: string | null;
    category: string;
  };
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

export function HonorsPage() {
  useAuth();

  const { data: assignments, isLoading } = useQuery({
    queryKey: ['my-honors'],
    queryFn: async () => {
      const response = await api.get<Assignment[]>('/gabbai/my');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  const now = new Date();
  const upcoming = assignments?.filter((a) => new Date(a.serviceDate) >= now) || [];
  const past = assignments?.filter((a) => new Date(a.serviceDate) < now) || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700';
      case 'declined': return 'bg-red-100 text-red-700';
      default: return 'bg-blue-100 text-blue-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">My Honors</h1>
          <p className="mt-2 text-gray-600">
            View your service honor assignments
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
          </div>
        ) : (!assignments || assignments.length === 0) ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
            <BookOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-1">No honor assignments</h3>
            <p className="text-gray-500">
              When you are assigned a service honor, it will appear here.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Upcoming Honors */}
            {upcoming.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming Honors</h2>
                <div className="grid gap-4 md:grid-cols-2">
                  {upcoming.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="bg-white rounded-xl shadow-sm border border-primary-200 p-5"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-gray-900">{assignment.honor.name}</h3>
                          {assignment.honor.hebrewName && (
                            <p className="text-sm text-gray-500">{assignment.honor.hebrewName}</p>
                          )}
                        </div>
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(assignment.status)}`}>
                          {assignment.status}
                        </span>
                      </div>
                      <div className="space-y-1.5 text-sm text-gray-600">
                        <p className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          {formatDate(assignment.serviceDate)}
                        </p>
                        {assignment.serviceName && (
                          <p className="text-gray-500">{assignment.serviceName}</p>
                        )}
                        {assignment.parsha && (
                          <p className="text-primary-600 font-medium">Parashat {assignment.parsha}</p>
                        )}
                        {assignment.notes && (
                          <p className="text-gray-400 italic">{assignment.notes}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Past Honors */}
            {past.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Past Honors</h2>
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Honor</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Parsha</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {past.map((assignment) => (
                        <tr key={assignment.id}>
                          <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                            {new Date(assignment.serviceDate).toLocaleDateString('en-US', {
                              month: 'short', day: 'numeric', year: 'numeric',
                            })}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{assignment.honor.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{assignment.serviceName || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{assignment.parsha || '-'}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${getStatusColor(assignment.status)}`}>
                              {assignment.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
